import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion, type Variants } from 'framer-motion';
import { X, AlertCircle, Loader2, Ban } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/toast.store';
import type { HouseholdDeclineReason } from './household.api';

/**
 * Cluster H v2 LD-H2-12/13 — modal overlay for declining a household
 * member. Replaces the inline two-click confirm in HouseholdMembersStep
 * with a deliberate modal flow + structured reason capture.
 *
 * Architecture mirrors ClientProfileModal (Fase 2.7.c):
 *   - createPortal to document.body
 *   - AnimatePresence wraps the inner mount
 *   - Inner keyed by hmId so re-opens get fresh state + focus init
 *   - Focus trap (Tab/Shift+Tab cycles within modal)
 *   - ESC + backdrop click close
 *   - useReducedMotion() degrades animations to identity
 *
 * Body shape per LD-H2-13:
 *   - 5 preset radio reasons (always_has_plan, moved_out_state, refused,
 *     no_contact, other)
 *   - Textarea is conditional: only when reason='other', and required
 *     (min 2 chars). Disabled otherwise.
 */

interface DeclineModalProps {
  /** When non-null the modal mounts and targets this hm for decline. */
  member: { hmId: string; fullName: string } | null;
  /** Closes the modal (cancel path). */
  onClose: () => void;
  /** Async submitter — parent owns the mutation + invalidation. Should
   *  resolve on success; reject on error (modal surfaces a toast). */
  onConfirm: (body: { reason: HouseholdDeclineReason; reasonText?: string | null }) => Promise<void>;
}

export function DeclineModal({ member, onClose, onConfirm }: DeclineModalProps): JSX.Element {
  const open = member !== null;
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && member ? (
        <DeclineModalInner
          key={member.hmId}
          member={member}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

interface InnerProps {
  member: { hmId: string; fullName: string };
  onClose: () => void;
  onConfirm: (body: { reason: HouseholdDeclineReason; reasonText?: string | null }) => Promise<void>;
}

function DeclineModalInner({ member, onClose, onConfirm }: InnerProps): JSX.Element {
  const t = useT();
  const reducedMotion = useReducedMotion();
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLInputElement>(null);
  const titleId = `decline-modal-${member.hmId}-title`;

  const [reason, setReason] = useState<HouseholdDeclineReason | ''>('');
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstFocusRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const canSubmit = (() => {
    if (!reason) return false;
    if (reason === 'other' && reasonText.trim().length < 2) return false;
    return !submitting;
  })();

  const handleSubmit = async (): Promise<void> => {
    if (!reason) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({
        reason: reason as HouseholdDeclineReason,
        reasonText: reason === 'other' ? reasonText.trim() : null,
      });
      toast.success(t('declineModal.success'));
      onClose();
    } catch (err) {
      const msg = (err as Error)?.message || String(err);
      setError(msg);
      toast.error(t('declineModal.failed'), msg);
    } finally {
      setSubmitting(false);
    }
  };

  const backdrop: Variants = reducedMotion
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.2 } },
        exit: { opacity: 0, transition: { duration: 0.15 } },
      };
  const modal: Variants = reducedMotion
    ? { hidden: { opacity: 1, scale: 1 }, visible: { opacity: 1, scale: 1 }, exit: { opacity: 1, scale: 1 } }
    : {
        hidden: { opacity: 0, scale: 0.96 },
        visible: {
          opacity: 1,
          scale: 1,
          transition: { type: 'spring', stiffness: 300, damping: 25 },
        },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } },
      };

  const REASONS: ReadonlyArray<{ key: HouseholdDeclineReason; labelKey: string }> = [
    { key: 'already_has_plan', labelKey: 'declineModal.reason.already_has_plan' },
    { key: 'moved_out_state',  labelKey: 'declineModal.reason.moved_out_state' },
    { key: 'refused',          labelKey: 'declineModal.reason.refused' },
    { key: 'no_contact',       labelKey: 'declineModal.reason.no_contact' },
    { key: 'other',            labelKey: 'declineModal.reason.other' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8">
      <motion.div
        className="absolute inset-0 bg-nav-navy/40 backdrop-blur-sm"
        variants={backdrop}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        variants={modal}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="relative max-h-[85vh] w-full max-w-md flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between gap-2 border-b border-nav-card px-5 py-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Ban className="h-4 w-4 text-nav-red shrink-0" aria-hidden />
            <p id={titleId} className="text-sm font-semibold text-nav-navy truncate">
              {t('declineModal.title', { name: member.fullName })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('declineModal.close')}
            className="p-1 rounded hover:bg-nav-soft text-nav-slate focus:outline-none focus:ring-2 focus:ring-nav-teal"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-sm text-nav-navy">{t('declineModal.prompt')}</p>

          <fieldset className="space-y-2">
            <legend className="sr-only">{t('declineModal.reasonLegend')}</legend>
            {REASONS.map((r, idx) => (
              <label
                key={r.key}
                className={cn(
                  'flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors',
                  reason === r.key
                    ? 'border-nav-teal bg-nav-tealTint'
                    : 'border-nav-card hover:bg-nav-soft',
                )}
              >
                <input
                  ref={idx === 0 ? firstFocusRef : undefined}
                  type="radio"
                  name="decline-reason"
                  value={r.key}
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                  className="mt-0.5 accent-nav-teal"
                />
                <span className="text-sm text-nav-navy">{t(r.labelKey as never)}</span>
              </label>
            ))}
          </fieldset>

          {reason === 'other' ? (
            <div>
              <label htmlFor={`decline-reason-text-${member.hmId}`} className="block text-xs font-medium text-nav-navy mb-1">
                {t('declineModal.otherTextLabel')}
              </label>
              <textarea
                id={`decline-reason-text-${member.hmId}`}
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder={t('declineModal.otherTextPlaceholder')}
                className="w-full text-sm rounded-md border border-nav-card p-2 focus:outline-none focus:ring-2 focus:ring-nav-teal"
                rows={3}
                maxLength={1000}
                required
                aria-required="true"
              />
              <p className="text-[11px] text-nav-slate mt-1">
                {t('declineModal.otherTextHint', { n: String(reasonText.length) })}
              </p>
            </div>
          ) : null}

          {error ? (
            <div role="alert" className="flex items-start gap-2 text-xs text-danger-700">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5" aria-hidden />
              <span>{error}</span>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-nav-card px-5 py-3 shrink-0 bg-nav-softer">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
            {t('declineModal.cancel')}
          </Button>
          <Button variant="danger" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('declineModal.confirm')}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
